import * as React from 'react';
import TextWithReferences from '../PageElements/TextWithReferences/TextWithReferences';
import { GameGapTextData, GameGapTextState } from '../Games.types';
import _ from 'underscore';
import { useAutoSaveOnPageLeave } from '../../../store/hooks';
import { GameStateDB } from '../../../data/datatypes';
import { useLocation } from 'react-router-dom';
import { getUrlWithTransientStateProperties } from '../Sessions/Session.logic';
import { getGameState, saveGameState } from '../../../services/DBService/DbService';

type Properties = {
    data: GameGapTextData;
};

const GapText = (props: Properties) => {
    const { items, wrongTerms, caption, desc } = props.data;

    const path = useLocation().pathname;
    const dbId: `game_${string}` = `game_${getUrlWithTransientStateProperties(path)}`;

    const { numTerms, rndTerms } = React.useMemo(() => {
        const terms = items.filter(item => item.type === 'term');
        const allTerms = terms.concat(wrongTerms.map(wrongTerm => { return { type: 'term', text: wrongTerm }; }));
        const randomized = _.shuffle(allTerms).map((item, index) => { return { text: item.text, index: index }; });
        return { numTerms: terms.length, rndTerms: randomized };
    }, [items, wrongTerms]);

    const [selected, setSelected] = React.useState<string[]>(new Array(numTerms).fill(''));
    const showResult = !selected.some(selectedValue => selectedValue === '');

    // reset internal state when the data changes (fix for displaying the game directly after each other)
    React.useEffect(() => {
        setSelected(new Array(numTerms).fill(''));
    }, [props.data]);

    useAutoSaveOnPageLeave(
        [selected],
        async () => {
            const gameState: GameStateDB = {
                _id: dbId,
                state: {
                    selected
                }
            };
            await saveGameState(gameState);
        },
        [path],
        async () => {
            const state = await getGameState(dbId);
            if (state) {
                setSelected((state.state as GameGapTextState).selected);
            }
        }
    );

    let counter: number = -1;
    let incorrectCounter: number = 0;
    let itemEls = items.map((item, index) => {
        switch (item.type) {
            case 'text':
                return <span key={index}><TextWithReferences text={item.text} /></span>;

            case 'term':
                counter++;
                const currentSelectIndex = counter;
                const currentValue = selected[currentSelectIndex];
                const somethingIsSelected = currentValue !== '';
                const pickedCorrectly = currentValue === item.text;

                let className: string = '';
                if (somethingIsSelected) {
                    if (pickedCorrectly) {
                        className = 'gap-text-correct';
                    } else {
                        incorrectCounter++;
                        className = 'gap-text-incorrect';
                    }
                }
                if (showResult === false) {
                    className = '';
                }

                const currentSelectChanged = (event: React.ChangeEvent<HTMLSelectElement>) => {
                    const newSelected = [...selected];
                    newSelected[currentSelectIndex] = event.target.value;
                    setSelected(newSelected);
                };

                return (
                    <select 
                        value={selected[currentSelectIndex]} 
                        onChange={currentSelectChanged} 
                        className={className}
                        key={index}
                    >
                        <option value="" disabled={true} />
                        {rndTerms.map((term, i) => <option value={term.text} key={i}>{term.text}</option>)}
                        <optgroup label="" />
                    </select>
                );

            default:
                return;
        }
    });

    let resultText: any = undefined;
    if (showResult === true) {
        if (incorrectCounter === 0) {
            resultText = (
                <p className="result-text result-text-correct">
                    Super, du hast alle Begriffe richtig zugeordnet!
                </p>
            );
        } else {
            resultText = (
                <p className="result-text result-text-incorrect">
                    {incorrectCounter} von {numTerms} Begriffen sind falsch. Bitte überprüfe die roten Begriffe noch einmal.
                </p>
            );
        }
    }

    return (
        <div className="game">
            <div className="game-content">
                <h1>{caption}</h1>
                <p><TextWithReferences text={desc || ''} /></p>
                <div className="gap-text">
                    {itemEls}
                    <br />
                    {resultText}
                </div>
            </div>
        </div>
    );
};

export default GapText;